import { useEffect, useState } from "react";
import { signOut } from "firebase/auth";
import { collection, getDocs, query, where } from "firebase/firestore";

import { auth, db } from "../FirebaseConfig";
import { useAuth } from "../lib/auth-context";

interface AdminContact {
  email: string;
  displayName: string;
}

/**
 * Shown when a user is authenticated but their Usuarios doc has no
 * roles assigned and isAdmin=false. We load the approver admins so the
 * user has a concrete person to ask for access. Falls back gracefully
 * if no `approver` is tagged — pulls every active admin instead, same
 * order of preference the backend uses for approval emails.
 */
export function NoAccess() {
  const { firebaseUser } = useAuth();
  const [admins, setAdmins] = useState<AdminContact[] | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(
          query(collection(db, "Usuarios"), where("isAdmin", "==", true))
        );
        const all: AdminContact[] = [];
        const tagged: AdminContact[] = [];
        snap.forEach((d) => {
          const data = d.data() as {
            email?: string;
            displayName?: string;
            isDeleted?: boolean;
            roles?: string[];
          };
          if (!data.email || data.isDeleted === true) return;
          const entry: AdminContact = {
            email: data.email,
            displayName: data.displayName ?? data.email,
          };
          all.push(entry);
          if (Array.isArray(data.roles) && data.roles.includes("approver")) {
            tagged.push(entry);
          }
        });
        setAdmins(tagged.length > 0 ? tagged : all);
      } catch {
        setAdmins([]);
      }
    })();
  }, []);

  const mailtoSubject = encodeURIComponent("Acceso a AltaJet Vuelos");
  const mailtoBody = encodeURIComponent(
    `Hola,\n\nMi cuenta ${firebaseUser?.email ?? ""} aún no tiene acceso a AltaJet Vuelos. ¿Pueden habilitarme?\n\nGracias.`
  );

  return (
    <main className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-6">
      <div className="rounded-lg border border-slate-200 bg-white p-8 text-center shadow-sm">
        <h1 className="text-xl font-semibold text-slate-900">
          Sin acceso
        </h1>
        <p className="mt-3 text-sm text-slate-600">
          Tu cuenta{" "}
          {firebaseUser?.email ? (
            <span className="font-mono">{firebaseUser.email}</span>
          ) : null}{" "}
          aún no tiene permisos asignados en AltaJet Vuelos.
        </p>
        {admins === null ? null : admins.length === 0 ? (
          <p className="mt-3 text-sm text-slate-500">
            Pide a un administrador que te habilite.
          </p>
        ) : (
          <div className="mt-4 space-y-2 text-left">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Contacta al administrador
            </p>
            <ul className="space-y-1 text-sm">
              {admins.map((a) => (
                <li key={a.email}>
                  <a
                    href={`mailto:${a.email}?subject=${mailtoSubject}&body=${mailtoBody}`}
                    className="text-brand-700 underline hover:text-brand-900"
                  >
                    {a.displayName}
                  </a>{" "}
                  <span className="font-mono text-xs text-slate-500">
                    &lt;{a.email}&gt;
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
        <button
          type="button"
          onClick={() => signOut(auth)}
          className="mt-6 rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          Cerrar sesión
        </button>
      </div>
    </main>
  );
}
